/**
 * 64.4, the shared half (decision 110; CLOCK_RFC lane A §6): the turn-counted rules both clocks read, converted
 * once, at the rule — never by reading a counter of the wrong clock. These three were the leaks round 0 named:
 * the action time limit ended a match by rewriting `turnNumber` past `turnLimit`; mount, melee, heal, build and
 * interact checked a movement budget the action clock never refills; ghost ages read a frozen `turnNumber`.
 * Pure and deterministic.
 */
import type { GameState, PlayerState } from '../types';
import { PHYS_DT } from '../constants';
import { inTurns, seconds } from './duration';

type Clocked = Pick<GameState, 'config'> & Partial<Pick<GameState, 'tick' | 'turnNumber'>>;

/** Seconds the action clock has run (its tick is in the state since R11 step 3). */
export const actionSeconds = (state: Clocked): number => (state.tick ?? 0) * PHYS_DT;

/**
 * The time limit: the turn match ends after `turnLimit` turns; the action clock after `actionTimeLimit` seconds. The
 * mode's turn-limit rule decides either way (the one win pipeline), read through this, never by rewriting a counter.
 */
export function timeLimitReached(state: Clocked): boolean {
  if (state.config.clock === 'action') {
    const limit = state.config.actionTimeLimit;
    return limit !== undefined && actionSeconds(state) >= limit;
  }
  const turnLimit = state.config.modeConfig?.turnLimit;
  return !!turnLimit && (state.turnNumber ?? 0) > turnLimit;
}

/**
 * The movement budget is a turn-family rule: a unit spends it within its turn. The action clock has no budget — its
 * actions are gated by their own cooldowns and reloads as they arrive on that clock (64.11, Phase 65) — so a budget
 * check there is always satisfied.
 */
export function canSpendMovement(state: Clocked | undefined, player: Pick<PlayerState, 'movementRemaining'>, cost: number): boolean {
  if (state?.config.clock === 'action') return true;
  return player.movementRemaining >= cost;
}

/**
 * "Now" for the knowledge ages that count turns (ghost and exposure markers' `turnSeen` against GHOST_MARKER_MAX_AGE
 * and EXPOSURE_MARKER_MAX_AGE): the turn number on the turn clock; on the action clock the turn number the match
 * started in plus the seconds run, converted at the rule by SECONDS_PER_TURN — so a marker made by a flyover on the
 * action clock ages and expires instead of living forever.
 */
export function knowledgeTurn(state: Clocked): number {
  const turn = state.turnNumber ?? 0;
  return state.config.clock === 'action' ? turn + inTurns(seconds(actionSeconds(state))) : turn;
}
